import {
  AccessDeniedError,
  BadRequestError,
  ConnectionError,
  HttpTypes,
  InternalServerError,
  TooManyRequestsError,
  UnexpectedError,
} from 'src/http';
import { httpResponseHandler } from 'src/http/http-response-handler';
import { fileUploadType } from 'src/types/banners';
import { homeBannerItem } from 'src/types/home-banners';
import { endpoints } from 'src/utils/axios';
import { Either } from 'src/utils/either';

export default class HomeBannersRepository {
  constructor(private api: HttpTypes.HttpClient) { }

  async findAllBanners(): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.personalization.homeBanners.list,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async findOne(
    id: string
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.personalization.homeBanners.one,
      params: { bannerPageId: id },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async update(
    id: string,
    data: homeBannerItem
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: endpoints.personalization.homeBanners.edit,
      params: { id },
      body: data,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async create(
    data: homeBannerItem
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: endpoints.personalization.homeBanners.create,
      body: data,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async uploadFile(
    file: fileUploadType
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: endpoints.files.upload,
      body: { ...file, optimizateImage: true },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async changeHomeBannerStatus(
    bannerId: string,
    status: 'active' | 'inactive'
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: endpoints.personalization.homeBanners.changeStatus(bannerId),
      body: { status }
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async deleteBanners(
    bannerPageIds: string[],
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'delete',
      url: endpoints.personalization.homeBanners.deleteBanners,
      body: { bannerPageIds }
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }
}
